import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  TablePagination,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  Divider,
  Snackbar,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Modal,
  Fade,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { useTheme } from "../../contexts/ThemeContext";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import {
  buscarTodasEmpresasPermitidas,
  excluirEmpresaPermitida,
  criarEmpresaPermitida,
} from "../../services/empresaPermitida";
import { EmpresaPermitida, EmpresaPermitidaCreateRequest } from "../../types";
import { formatarCpf, formatarCnpj } from "../../utils/normalization";

const ListarEmpresasPermitidas: React.FC = () => {
  const { darkMode } = useTheme();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(muiTheme.breakpoints.down("md"));

  const [empresas, setEmpresas] = useState<EmpresaPermitida[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [empresaParaExcluir, setEmpresaParaExcluir] = useState<EmpresaPermitida | null>(null);
  const [excluindo, setExcluindo] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error" | "warning" | "info">("info");

  // Modal de cadastro
  const [modalOpen, setModalOpen] = useState(false);
  const [novaEmpresa, setNovaEmpresa] = useState<EmpresaPermitidaCreateRequest>({
    tipoInscricao: "CPF",
    inscricao: "",
  });
  const [criando, setCriando] = useState(false);

  const showMessage = (message: string, severity: "success" | "error" | "warning" | "info") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const loadEmpresas = async () => {
    try {
      setLoading(true);
      setError("");

      // Buscar todas as empresas sem paginação e sem limite
      const empresas = await buscarTodasEmpresasPermitidas();

      setEmpresas(empresas);
      setTotal(empresas.length);
    } catch (err: any) {
      console.error("Erro ao carregar empresas permitidas:", err);
      setError("Erro ao carregar empresas permitidas");
      showMessage("Erro ao carregar empresas permitidas", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEmpresas();
  }, []);

  const formatarInscricao = (tipo: string, inscricao: string) => {
    if (tipo === "CPF") {
      return formatarCpf(inscricao);
    } else {
      return formatarCnpj(inscricao);
    }
  };

  // Filtrar empresas localmente
  const filteredEmpresas = empresas.filter((empresa) => {
    if (!searchTerm) return true;
    
    const inscricaoFormatada = formatarInscricao(empresa.tipoInscricao, empresa.inscricao);
    // Buscar tanto nos números puros quanto na formatação
    return empresa.inscricao.includes(searchTerm) || inscricaoFormatada.includes(searchTerm);
  });

  // Paginação local
  const empresasPaginadas = filteredEmpresas.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(0); // Reset para primeira página ao buscar
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteClick = (empresa: EmpresaPermitida) => {
    setEmpresaParaExcluir(empresa);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!empresaParaExcluir) return;

    try {
      setExcluindo(true);
      await excluirEmpresaPermitida(empresaParaExcluir.id);
      showMessage("Empresa permitida excluída com sucesso", "success");
      await loadEmpresas();
    } catch (err: any) {
      console.error("Erro ao excluir empresa permitida:", err);
      showMessage("Erro ao excluir empresa permitida", "error");
    } finally {
      setExcluindo(false);
      setDeleteDialogOpen(false);
      setEmpresaParaExcluir(null);
    }
  };

  const handleCreateEmpresa = async () => {
    try {
      setCriando(true);
      await criarEmpresaPermitida(novaEmpresa);
      showMessage("Empresa permitida cadastrada com sucesso", "success");
      setModalOpen(false);
      setNovaEmpresa({ tipoInscricao: "CPF", inscricao: "" });
      await loadEmpresas();
    } catch (err: any) {
      console.error("Erro ao criar empresa permitida:", err);
      showMessage("Erro ao cadastrar empresa permitida", "error");
    } finally {
      setCriando(false);
    }
  };

  const getTipoIcon = (tipo: string) => {
    return tipo === "CPF" ? <PersonIcon /> : <BusinessIcon />;
  };

  const getTipoColor = (tipo: string) => {
    return tipo === "CPF" ? "primary" : "secondary";
  };

  if (loading && empresas.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Card
        sx={{
          backgroundColor: darkMode ? "#1e1e1e" : "#ffffff",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        }}
      >
        <CardContent>
          <Stack spacing={3}>
            {/* Header */}
            <Box>
              <Typography variant="h4" component="h1" gutterBottom>
                Empresas Permitidas
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total de empresas permitidas: {empresas.length}
              </Typography>
            </Box>

            <Divider />

            {/* Filtros e Ações */}
            <Stack
              direction={isMobile ? "column" : "row"}
              spacing={2}
              alignItems={isMobile ? "stretch" : "center"}
              justifyContent="space-between"
            >
              <TextField
                placeholder="Pesquisar por inscrição (CPF/CNPJ)..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  minWidth: isMobile ? "100%" : 300,
                  flexGrow: 1,
                  maxWidth: isMobile ? "100%" : "calc(100% - 180px)"
                }}
              />

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setModalOpen(true)}
                sx={{ minWidth: 160 }}
              >
                Nova Empresa
              </Button>
            </Stack>

            {/* Tabela */}
            {error ? (
              <Alert severity="error">{error}</Alert>
            ) : (
              <TableContainer component={Paper} sx={{ backgroundColor: darkMode ? "#2d2d2d" : "#ffffff" }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Tipo</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Inscrição</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {empresasPaginadas.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          <Typography variant="body2" color="text.secondary">
                            Nenhuma empresa permitida encontrada
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      empresasPaginadas.map((empresa) => (
                      <TableRow key={empresa.id} hover>
                        <TableCell>
                          <Chip
                            icon={getTipoIcon(empresa.tipoInscricao)}
                            label={empresa.tipoInscricao}
                            color={getTipoColor(empresa.tipoInscricao) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {formatarInscricao(empresa.tipoInscricao, empresa.inscricao)}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Excluir">
                            <IconButton
                              color="error"
                              onClick={() => handleDeleteClick(empresa)}
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Paginação */}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredEmpresas.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Linhas por página:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
              }
            />
          </Stack>
        </CardContent>
      </Card>

      {/* Modal de Confirmação de Exclusão */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Tem certeza que deseja excluir esta empresa permitida?
            <br />
            <strong>
              {empresaParaExcluir?.tipoInscricao}: {empresaParaExcluir && formatarInscricao(empresaParaExcluir.tipoInscricao, empresaParaExcluir.inscricao)}
            </strong>
            <br />
            Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={excluindo}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={excluindo}
            startIcon={excluindo ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {excluindo ? "Excluindo..." : "Excluir"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Cadastro */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        aria-labelledby="create-empresa-modal"
      >
        <Fade in={modalOpen}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: isMobile ? "90%" : 400,
              bgcolor: darkMode ? "#2d2d2d" : "#ffffff",
              borderRadius: 2,
              boxShadow: 24,
              p: 4,
            }}
          >
            <Typography variant="h6" component="h2" gutterBottom>
              Nova Empresa Permitida
            </Typography>
            <Stack spacing={3} sx={{ mt: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Inscrição</InputLabel>
                <Select
                  value={novaEmpresa.tipoInscricao}
                  onChange={(e) =>
                    setNovaEmpresa({
                      ...novaEmpresa,
                      tipoInscricao: e.target.value as "CPF" | "CNPJ",
                      inscricao: "", // Limpar inscrição ao mudar tipo
                    })
                  }
                  label="Tipo de Inscrição"
                >
                  <MenuItem value="CPF">CPF</MenuItem>
                  <MenuItem value="CNPJ">CNPJ</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Inscrição"
                value={
                  novaEmpresa.inscricao
                    ? novaEmpresa.tipoInscricao === "CPF"
                      ? formatarCpf(novaEmpresa.inscricao)
                      : formatarCnpj(novaEmpresa.inscricao)
                    : ""
                }
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const valor = e.target.value.replace(/\D/g, "");
                  setNovaEmpresa({ ...novaEmpresa, inscricao: valor });
                }}
                placeholder={
                  novaEmpresa.tipoInscricao === "CPF"
                    ? "Digite apenas os números do CPF"
                    : "Digite apenas os números do CNPJ"
                }
                inputProps={{
                  maxLength: novaEmpresa.tipoInscricao === "CPF" ? 14 : 18, // Aumentado para incluir formatação
                }}
              />

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  onClick={() => setModalOpen(false)}
                  disabled={criando}
                  variant="outlined"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleCreateEmpresa}
                  disabled={criando || !novaEmpresa.inscricao}
                  variant="contained"
                  startIcon={criando ? <CircularProgress size={16} /> : <AddIcon />}
                >
                  {criando ? "Cadastrando..." : "Cadastrar"}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Fade>
      </Modal>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ListarEmpresasPermitidas;
